import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FaHome, FaBus, FaCar, FaTimesCircle } from "react-icons/fa";
import { renderToStaticMarkup } from "react-dom/server";

// Fix for the annoying default Leaflet icon issue...
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Custom Home Icon
const homeIconMarkup = renderToStaticMarkup(<FaHome color="blue" size={30} />);
const homeIcon = L.divIcon({
  html: homeIconMarkup,
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

// Dynamic marker icon based on status
function getMarkerIcon(trip, type) {
  let iconMarkup;
  switch (true) {
    case trip.status === "Bus":
      iconMarkup = renderToStaticMarkup(
        <FaBus color={type === "from" ? "green" : "red"} size={25} />,
      );
      break;
    case trip.status === "Unallocated":
      iconMarkup = renderToStaticMarkup(<FaBus color="gray" size={25} />);
      break;
    case trip.status.startsWith("Vol Car - "):
      iconMarkup = renderToStaticMarkup(
        <FaCar color={type === "from" ? "green" : "red"} size={25} />,
      );
      break;
    case trip.status.startsWith("Cancelled - "):
      iconMarkup = renderToStaticMarkup(
        <FaTimesCircle color="red" size={25} />,
      );
      break;
    case trip.status.startsWith("Refused - "):
      iconMarkup = renderToStaticMarkup(
        <FaTimesCircle
          color="red"
          size={25}
          style={{ transform: "rotate(90deg)" }}
        />,
      );
      break;
    default:
      iconMarkup = renderToStaticMarkup(
        <FaBus color={type === "from" ? "green" : "red"} size={25} />,
      );
  }

  return L.divIcon({
    html: iconMarkup,
    className: "",
    iconSize: [25, 25],
    iconAnchor: [12, 25],
  });
}

function Recenter({ trip }) {
  const map = useMap();

  useEffect(() => {
    if (trip) {
      const bounds = [
        [trip.from.lat, trip.from.lng],
        [trip.to.lat, trip.to.lng],
      ];
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [trip, map]);

  return null;
}

export default function MapContent({ trip }) {
  const [defaultCenter, setDefaultCenter] = useState(null);
  const isValidCoord = (value) =>
    typeof value === "number" && !isNaN(value);
  
  const hasFromCoords =
  isValidCoord(trip?.from?.lat) &&
  isValidCoord(trip?.from?.lng);

  const hasToCoords =
  isValidCoord(trip?.to?.lat) &&
  isValidCoord(trip?.to?.lng);

  useEffect(() => {
    const paxData = localStorage.getItem("paxData");
    if (paxData) {
      try {
        const parsed = JSON.parse(paxData);
        if (parsed[0]?.Lat && parsed[0]?.Lon) {
          setDefaultCenter([parsed[0].Lat, parsed[0].Lon]);
        } else {
          setDefaultCenter([51.878, -2.163]);
        }
      } catch (err) {
        console.error("Error parsing paxData:", err);
        setDefaultCenter([51.878, -2.163]);
      }
    } else {
      setDefaultCenter([51.878, -2.163]);
    }
  }, []);

  if (!defaultCenter) return <p>Loading map...</p>;

  return (
    <section className="left map-container">
      <MapContainer
        center={defaultCenter}
        zoom={13}
        style={{ height: "100%", width: "100%", borderRadius: "8px" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <Marker position={defaultCenter} icon={homeIcon}>
          <Popup>Home Location</Popup>
        </Marker>

        {trip && (
          <>
            {hasFromCoords && (
              <Marker
                position={[trip.from.lat, trip.from.lng]}
                icon={getMarkerIcon(trip, "from")}
              >
                <Popup>Pick up: {trip.from.name}</Popup>
              </Marker>
            )}

            {hasToCoords && (
              <Marker
                position={[trip.to.lat, trip.to.lng]}
                icon={getMarkerIcon(trip, "to")}
              >
                <Popup>Drop off: {trip.to.name}</Popup>
              </Marker>
            )}

            {hasFromCoords && hasToCoords && (
              <>
                <Polyline
                  positions={[
                    [trip.from.lat, trip.from.lng],
                    [trip.to.lat, trip.to.lng],
                  ]}
                  pathOptions={{ color: "green", weight: 4 }}
                />

                <Recenter trip={trip} />
              </>
            )}
          </>
        )}
      </MapContainer>
    </section>
  );
}
